import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Button,
  Stack,
  CircularProgress,
  Skeleton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { FormData } from '../CreateFormDialog';
import { templateApi } from '../../../services';
import { wizardOutlinedButtonSx } from '../wizardStyles';
import { TemplatePreviewArtwork } from '../../../components/shared/Icons';
import Toast from '../../../components/shared/Toast';
import { FormTemplate, countTemplateFields } from '../templateConfig';
import TemplateCopyButton from './TemplateCopyButton';
import { ADMIN_COLORS } from '../../../theme';

const ACCENT = '#4F46E5';

interface TemplateSelectionProps {
  onNext: (data: Partial<FormData>) => void;
  onBack: () => void;
  initialData: FormData;
  tenantId: string;
  submitting?: boolean;
}

export const TemplateSelection: React.FC<TemplateSelectionProps> = ({
  onNext,
  onBack,
  initialData,
  tenantId,
  submitting,
}) => {

  const [selectedIndex, setSelectedIndex] = useState<number | 'blank' | null>(null);
  const [templates, setTemplates] = useState<FormTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [advancing, setAdvancing] = useState(false);
  const [copyResult, setCopyResult] = useState<boolean | null>(null);

  useEffect(() => {
    loadTemplates();
  }, [initialData.formType, tenantId]);

  const wasSubmitting = React.useRef(false);
  useEffect(() => {

    if (wasSubmitting.current && !submitting) {
      setAdvancing(false);
      setSelectedIndex(null);
    }
    wasSubmitting.current = !!submitting;
  }, [submitting]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const filters: any = {
        formType: initialData.formType,
      };

      const fetchedTemplates = await templateApi.getTemplates(tenantId, filters);

      setTemplates(
        (fetchedTemplates || []).map((t: any) => ({ ...t, id: t.id ?? t._id })),
      );
    } catch (error) {
      console.error('Error loading templates:', error);
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (template: FormTemplate | null, index: number | 'blank') => {
    if (advancing) return; // ignore extra clicks while a selection is in flight
    setAdvancing(true);
    if (template) {
      setSelectedIndex(index);
      setTimeout(() => {
        onNext({ template });
      }, 300);
    } else {
      const blankTemplate: FormTemplate = {
        id: 'blank',
        name: 'Blank Form',
        description: 'Start from scratch with an empty form',
        formType: initialData.formType!,
        targetDevice: initialData.targetDevice!,
        configuration: {},
        steps: initialData.formType === 'multistep' ? ['Step 1'] : [],
      };
      setSelectedIndex('blank');
      setTimeout(() => {
        onNext({ template: blankTemplate });
      }, 300);
    }
  };

  if (loading) {
    return (
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {Array.from({ length: 3 }).map((_, i) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={i}>
            <Box
              sx={{
                border: `1px solid ${ADMIN_COLORS.border}`,
                borderRadius: '14px',
                overflow: 'hidden',
                bgcolor: ADMIN_COLORS.surface,
              }}
            >
              <Box sx={{ p: 2, pb: 1.25, bgcolor: ADMIN_COLORS.headBg }}>
                <Skeleton variant="rounded" width="100%" height={88} />
              </Box>
              <Box sx={{ p: 2, pt: 1.5 }}>
                <Skeleton variant="text" width="60%" height={20} />
                <Skeleton variant="text" width="90%" height={16} sx={{ mt: 0.5 }} />
                <Skeleton variant="text" width="70%" height={16} />
                <Box sx={{ pt: 1.25, mt: 1, borderTop: `1px solid ${ADMIN_COLORS.divider}` }}>
                  <Skeleton variant="text" width="45%" height={14} />
                </Box>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
    );
  }

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }} key="blank">
          <Box
            onClick={() => !advancing && handleSelect(null, 'blank')}
            sx={{
              position: 'relative',
              cursor: advancing ? 'default' : 'pointer',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              border: '1.5px dashed',
              borderColor: selectedIndex === 'blank' ? ADMIN_COLORS.accent : ADMIN_COLORS.borderStrong,
              borderRadius: '14px',
              bgcolor: selectedIndex === 'blank' ? ADMIN_COLORS.accentSoft : ADMIN_COLORS.headBg,
              opacity: advancing && selectedIndex !== 'blank' ? 0.5 : 1,
              transition: 'border-color 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease',
              '&:hover': advancing ? {} : { borderColor: ADMIN_COLORS.accent },
            }}
          >
            <Box
              sx={{
                flexGrow: 1,
                minHeight: 172,
                p: 2.5,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
              }}
            >
              {selectedIndex === 'blank' && advancing ? (
                <CircularProgress size={26} sx={{ mb: 1.5, color: ADMIN_COLORS.accent }} />
              ) : (
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 1.5,
                    bgcolor: ADMIN_COLORS.accentSoft,
                  }}
                >
                  <AddIcon sx={{ fontSize: 22, color: ADMIN_COLORS.accent }} />
                </Box>
              )}
              <Typography sx={{ fontSize: 13.5, fontWeight: 700, mb: 0.5 }}>
                Start from Scratch
              </Typography>
              <Typography sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.5 }}>
                Create a new form with no pre-filled fields
              </Typography>
            </Box>
          </Box>
        </Grid>

        {templates.map((template, index) => {
          const isSelected = selectedIndex === index;
          const fieldCount = countTemplateFields(template.configuration);
          const stepCount = template.steps?.length ?? 0;
          const metaLabel =
            fieldCount > 0
              ? `${fieldCount} field${fieldCount === 1 ? '' : 's'} included`
              : stepCount > 1
              ? `${stepCount} steps`
              : 'Start from scratch';

          return (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={template.id || index}>
              <Box
                onClick={() => !advancing && handleSelect(template, index)}
                sx={{
                  position: 'relative',
                  cursor: advancing ? 'default' : 'pointer',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  border: '1.5px solid',
                  borderColor: isSelected ? ADMIN_COLORS.accent : ADMIN_COLORS.border,
                  borderRadius: '14px',
                  overflow: 'hidden',
                  bgcolor: ADMIN_COLORS.surface,
                  opacity: advancing && !isSelected ? 0.5 : 1,
                  transition: 'border-color 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease',
                  boxShadow: isSelected
                    ? `0 0 0 3px ${ADMIN_COLORS.accentRing}`
                    : '0 1px 2px rgba(16, 24, 40, 0.04)',
                  '&:hover': advancing ? {} : { borderColor: ADMIN_COLORS.accent },
                }}
              >
                {isSelected && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 12,
                      right: 12,
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      bgcolor: '#10B981',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 2,
                      boxShadow: '0 2px 6px rgba(16, 185, 129, 0.35)',
                    }}
                  >
                    <CheckIcon sx={{ fontSize: 14, color: '#fff' }} />
                  </Box>
                )}
                {isSelected && advancing && (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: `color-mix(in srgb, ${ADMIN_COLORS.surface} 70%, transparent)`,
                      zIndex: 1,
                    }}
                  >
                    <CircularProgress size={26} sx={{ color: ADMIN_COLORS.accent }} />
                  </Box>
                )}

                {!isSelected && (
                  <TemplateCopyButton
                    template={template}
                    disabled={advancing}
                    onCopied={setCopyResult}
                  />
                )}

                <Box sx={{ p: 2, pb: 1.25, bgcolor: ADMIN_COLORS.headBg }}>
                  <TemplatePreviewArtwork fieldCount={fieldCount} stepCount={stepCount} accent={ACCENT} />
                </Box>

                <Box sx={{ p: 2, pt: 1.5, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 700, mb: 0.5 }}>
                    {template.name}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: 'text.secondary',
                      lineHeight: 1.5,
                      mb: 1.5,
                      flexGrow: 1,
                    }}
                  >
                    {template.description || 'A ready-made layout you can customize.'}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      pt: 1.25,
                      borderTop: `1px solid ${ADMIN_COLORS.divider}`,
                    }}
                  >
                    <DescriptionOutlinedIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
                    <Typography sx={{ fontSize: 11.5, color: 'text.secondary', fontWeight: 500 }}>
                      {metaLabel}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
          );
        })}
      </Grid>
      <Stack direction="row" spacing={2} justifyContent="flex-start">
        <Button variant="outlined" onClick={onBack} disabled={advancing} sx={wizardOutlinedButtonSx}>
          Back
        </Button>
      </Stack>
      <Toast
        open={copyResult !== null}
        message={
          copyResult
            ? 'Template config copied — use "Paste config" to create a form from it'
            : 'Could not copy the config. Check clipboard permissions.'
        }
        severity={copyResult ? 'success' : 'error'}
        onClose={() => setCopyResult(null)}
      />
    </Box>
  );
};

